import type { RoleModule } from '../types';
import { ManagerTodayPage } from './today/ManagerTodayPage';
import { ManagerLedgerPage } from './ledger/ManagerLedgerPage';
import { ManagerAlertsPage } from './alerts/ManagerAlertsPage';
import { ManagerAuditPage } from './audit/ManagerAuditPage';
import { ManagerDigestPage } from './digest/ManagerDigestPage';
import { ManagerReportsPage } from './reports/ManagerReportsPage';
import { ManagerRevenuePage } from './reports/ManagerRevenuePage';
import { ManagerVisitsPage } from './reports/ManagerVisitsPage';
import { ManagerSourcesPage } from './reports/ManagerSourcesPage';
import { ManagerInsurancePage } from './reports/ManagerInsurancePage';
import { ManagerEditsPage } from './reports/ManagerEditsPage';
import { ManagerChartsPage } from './reports/ManagerChartsPage';
import { ManagerTrendsPage } from './reports/ManagerTrendsPage';
import { ManagerStockPage } from './reports/ManagerStockPage';
import { ManagerSettingsPage } from './settings/ManagerSettingsPage';
import { ManagerFacilityPage } from './settings/ManagerFacilityPage';
import { ManagerBrandingPage } from './settings/ManagerBrandingPage';
import { ManagerDomainsPage } from './settings/ManagerDomainsPage';
import { ManagerRecordingPage } from './settings/ManagerRecordingPage';
import { ManagerPatientIdsPage } from './settings/ManagerPatientIdsPage';
import { ManagerPrivacyPage } from './settings/ManagerPrivacyPage';
import { ManagerModulesPage } from './settings/ManagerModulesPage';
import { ManagerMoneyPage } from './settings/ManagerMoneyPage';
import { ManagerSubscriptionPage } from './settings/ManagerSubscriptionPage';
import { ManagerRolesPage } from './settings/ManagerRolesPage';
import { ManagerWalletPage } from './settings/ManagerWalletPage';
import { ManagerMessagesPage } from './settings/ManagerMessagesPage';
import { ManagerFlowsPage } from './settings/ManagerFlowsPage';

/**
 * The clinic-manager surface's routes and door overrides. See ../types.ts.
 * Every route path here must start with `/clinic-manager/`.
 *
 * WHY THE DOORS ARE OVERRIDDEN. `designRoles.ts` points this role's five doors
 * at the closest legacy pages that existed — `/dashboard` for the day,
 * `/notifications` for approvals, `/audit-log` for the log — and leaves Reports
 * without a route at all, because `/reports` is commented out of the router.
 * Those were the honest answers before this surface existed. Now that it does,
 * the doors lead to the manager's own screens, which is what `doorRoutes` is
 * for: a role redirects its own doors without touching the shared config, and
 * without a merge conflict with eight other agents.
 *
 * Settings IS overridden now: `designRoles.ts` points it at `/settings`, the
 * legacy admin page, and the manager's own thirteen doors exist under
 * `/clinic-manager/settings`. Nine of those thirteen rooms are later batches
 * and say "soon" on their own card rather than leading anywhere.
 *
 * WHY THE LEDGER AND THE DIGEST LIVE UNDER `/clinic-manager/today/`. MaieRail
 * lights a door when the path equals its route or starts with `${route}/`, so a
 * screen parked at the root of the surface lights NOTHING — the ledger and the
 * digest both stood with an unlit rail, which reads as "you have left the app".
 * Both are extensions of the day (Today's branch row says "open the ledger ›"
 * and its digest card opens the digest), so the Today door is the one that owns
 * them, and nesting them under it is what says so.
 */
const clinic_manager: RoleModule = {
  routes: [
    { path: '/clinic-manager/today', element: <ManagerTodayPage /> },
    { path: '/clinic-manager/today/ledger', element: <ManagerLedgerPage /> },
    { path: '/clinic-manager/alerts', element: <ManagerAlertsPage /> },
    { path: '/clinic-manager/audit', element: <ManagerAuditPage /> },
    { path: '/clinic-manager/today/digest', element: <ManagerDigestPage /> },
    { path: '/clinic-manager/reports', element: <ManagerReportsPage /> },
    { path: '/clinic-manager/reports/revenue', element: <ManagerRevenuePage /> },
    { path: '/clinic-manager/reports/visits', element: <ManagerVisitsPage /> },
    { path: '/clinic-manager/reports/sources', element: <ManagerSourcesPage /> },
    { path: '/clinic-manager/reports/insurance', element: <ManagerInsurancePage /> },
    { path: '/clinic-manager/reports/edits', element: <ManagerEditsPage /> },
    { path: '/clinic-manager/reports/charts', element: <ManagerChartsPage /> },
    { path: '/clinic-manager/reports/trends', element: <ManagerTrendsPage /> },
    { path: '/clinic-manager/reports/stock', element: <ManagerStockPage /> },
    { path: '/clinic-manager/settings', element: <ManagerSettingsPage /> },
    { path: '/clinic-manager/settings/facility', element: <ManagerFacilityPage /> },
    { path: '/clinic-manager/settings/branding', element: <ManagerBrandingPage /> },
    { path: '/clinic-manager/settings/domains', element: <ManagerDomainsPage /> },
    { path: '/clinic-manager/settings/recording', element: <ManagerRecordingPage /> },
    { path: '/clinic-manager/settings/patient-ids', element: <ManagerPatientIdsPage /> },
    { path: '/clinic-manager/settings/privacy', element: <ManagerPrivacyPage /> },
    { path: '/clinic-manager/settings/modules', element: <ManagerModulesPage /> },
    { path: '/clinic-manager/settings/money', element: <ManagerMoneyPage /> },
    { path: '/clinic-manager/settings/subscription', element: <ManagerSubscriptionPage /> },
    { path: '/clinic-manager/settings/roles', element: <ManagerRolesPage /> },
    { path: '/clinic-manager/settings/wallet', element: <ManagerWalletPage /> },
    { path: '/clinic-manager/settings/messages', element: <ManagerMessagesPage /> },
    { path: '/clinic-manager/settings/flows', element: <ManagerFlowsPage /> },
  ],
  doorRoutes: {
    today: '/clinic-manager/today',
    alerts: '/clinic-manager/alerts',
    reports: '/clinic-manager/reports',
    audit: '/clinic-manager/audit',
    settings: '/clinic-manager/settings',
  },
};

export default clinic_manager;
